import React from 'react';
import styled from 'styled-components';
import ProductCard from '@99designs/apps/spa/common/Product/ProductCard';
import brandPack from '@99designs/apps/spa/images/99dstudio-brandpack.png';
import { useProductsQuery } from '@99designs/apps/spa/pages/create/common/Products.generated';
import { ButtonWrapper } from '@99designs/apps/spa/pages/create/components/Button';
import ContentSetting from '@99designs/apps/spa/pages/create/components/ContentSetting';
import { isFixedPriceFragment } from '@99designs/apps/spa/pages/create/util/filterProducts';
import { Box, Button, Heading, Link, PageHeading, Stack, Text } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { LoadableElement } from '@99designs/frontend-components/components/LoadableElement';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { CurrencyCode } from '@99designs/i18n';
import { linkTo } from '@99designs/routing';
import { ViewProductDetails } from '@99designs/spa-common';
import { packageToProductSummary } from '@99designs/spa-common';
import { Price } from '@99designs/spa-common';

const CardContainer = styled.div`
    > * {
        background-color: white;
        z-index: 20;
    }
`;

const ImageContainer = styled.div`
    position: absolute;
    width: 90%;
    z-index: 10;
    overflow-y: hidden;

    ${mediaQuery.minimum('normal')} {
        top: 10px;
        left: 300px;
    }

    ${mediaQuery.between('medium', 'normal')} {
        bottom: 100px;
        left: 90px;
    }

    ${mediaQuery.only('small')} {
        display: none;
    }
`;

export default function FullServiceBrandingProductSelection() {
    const { error, data, loading } = useProductsQuery({
        variables: { input: { names: ['full-service-branding'] } },
    });

    if (error) {
        throw error;
    }

    if (loading || !data) {
        return <LoadableElement variations={['fade-in']} isLoading={true} />;
    }

    const product = data.products[0];

    if (!isFixedPriceFragment(product)) {
        return null;
    }

    const packageForProductSummary = {
        title: product.title,
        description: product.description,
        amountInCents: product.pricing.amount.amountInCents,
        currency: product.pricing.amount.currency,
    };

    return (
        <ContentSetting>
            <Box marginTop="1.5bu" marginBottom="1.5bu">
                <PageHeading>{__('Get started with our Studio pack')}</PageHeading>
            </Box>

            <Box marginTop={['none', '2bu']} position="relative">
                <Stack space="1.5bu">
                    <CardContainer key={product.key}>
                        <ProductCard
                            rightColumn={
                                <Stack space="1bu">
                                    <Box display="flex" justifyContent="center">
                                        <ViewProductDetails
                                            features={product.details}
                                            additionalDetails={product.additionalDetails}
                                            productSummary={packageToProductSummary({
                                                ...packageForProductSummary,
                                            })}
                                            image={product.image}
                                            headerVideo={product.headerVideo}
                                        />
                                    </Box>
                                    <ButtonWrapper>
                                        <Button
                                            size="large"
                                            appearance="primary"
                                            width="fill"
                                            href={linkTo.briefCreate(product.key)}
                                            type="button"
                                        >
                                            {__('Start brief')}
                                        </Button>
                                    </ButtonWrapper>
                                </Stack>
                            }
                            productSummary={packageToProductSummary(packageForProductSummary)}
                            features={product.details}
                            hasLeftBorder={false}
                        />
                    </CardContainer>
                </Stack>
            </Box>

            <Box marginTop="8bu" maxWidth="620px">
                <Stack space="0.75bu">
                    <Heading appearanceLevel={5} accessibilityLevel={5}>
                        {__('Don’t quite see what you’re looking for?')}
                    </Heading>
                    <div>
                        <FormattedReactMessage
                            message={__(
                                `We can provide a range of design services from strategy workshops, websites, packaging, social and campaigns. <Link>Book a free consult with the 99d Studio team</Link> and we can advise on how best we can support you.`
                            )}
                            replacements={{
                                Link: (
                                    <Link href="https://calendly.com/laura-circlemaker/brand-consult" />
                                ),
                            }}
                        />
                        &nbsp;
                        <Text weight="bold">
                            <FormattedReactMessage
                                message={__('From <Price>price</Price>')}
                                replacements={{
                                    Price: (
                                        <Price
                                            amount={product.pricing.amount.amountInCents}
                                            currency={
                                                product.pricing.amount.currency as CurrencyCode
                                            }
                                        />
                                    ),
                                }}
                            />
                        </Text>
                    </div>
                </Stack>
            </Box>
            <ImageContainer>
                <img alt="99dstudio Brandpack" src={brandPack} />
            </ImageContainer>
        </ContentSetting>
    );
}
